/**
 * Converts a string to camelCase, splitting on any non-alphanumeric character.
 * The first word is converted to lowercase, and each subsequent word has its
 * first letter capitalized.
 *
 * @param {string} str - The input string to be converted to camelCase.
 *                        Any non-alphanumeric characters are treated as word separators.
 * @returns {string} The camelCase version of the input string.
 *
 * @example
 * // Basic usage:
 * toCamelCase('a-nice-string'); // Returns: 'aNiceString'
 *
 * @example
 * // Handles multiple non-alphanumeric separators:
 * toCamelCase('__nice---string_123'); // Returns: 'niceString123'
 *
 * @example
 * // Works with strings containing only non-alphanumeric characters:
 * toCamelCase('--__--'); // Returns: ''
 *
 * @example
 * // Works with uppercase letters in the input:
 * toCamelCase('A-nice_STRING'); // Returns: 'aNiceString'
 *
 * @example
 * // Handles empty input:
 * toCamelCase(''); // Returns: ''
 */
export const toCamelCase = (str: string): string => {
  return str
    .split(/[^a-zA-Z0-9]/)
    .filter(Boolean)
    .map((word, index) =>
      index === 0
        ? word.toLowerCase() // Keep the first word lowercase
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('')
}
